import { useCallback, useState } from 'react';
import type { QueryColumn, QueryResultSet } from '../../../../types/queryExecution';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { ResultsExportFormat } from '../../../../types/resultsExport';
import { useAppStore } from '../../../../store/appStore';
import { exportResults } from '../services/resultsExportApi';
import { fetchIdentityColumnsForResultSet } from '../utils/identityColumns';
import type { InsertIdentityMode } from '../utils/insertStatementBuilder';
import { resultSetToCsv, resultSetToMarkdown, resultSetToInsert, resultSetToHtml, writeRichToClipboard } from '../utils/clipboardFormatters';
import { toast } from '../../../../components/ui/toaster';
import { writeClipboardText } from '../../../../lib/clipboard';
import { showExportSuccessToast } from '../utils/exportSuccessToast';
import { checkExportLimit, calculateTotalExportRows } from '../utils/personalModeLimits';
import { fetchFullBufferRows } from '../hooks/useCellSelection';
// T5 — WKT copy parity: the renderer reaches the decode core only through
// this lazy loader (bundle isolation — see Docs/Spatial-Support-Plan.md
// Global Constraints).
import { loadSpatialDecoder } from '../../spatial/decode/loadSpatialDecoder';
import type { GridExportOutcome } from '../store/gridExportRegistry';

type UseExportCopyParams = {
  exportableResultSets: QueryResultSet[];
  activeExportResultSetIndex: number;
  canExport: boolean;
  canExportFull: boolean;
  engineId?: string;
  isWindowed: boolean;
  wdBufferRows: Record<string, unknown>[];
  wdBufferStart: number;
  activeResultSetColumns: { id?: string; name: string }[];
  effectiveTotalRowCount: number;
  getCellValueFn?: (rowIndex: number, columnId: string) => Promise<{ value: unknown; generation: number }>;
  sourceGeneration: number;
  /** T5 — native-spatial column ids (WKT copy/export parity). Decoded via `engineId`. */
  nativeSpatialColumnIds?: ReadonlySet<string>;
  /** D2 — identity lookup for generated INSERTs. Without these the output stays identity-unaware. */
  connectionId?: string | null;
  database?: string | null;
};

type UseExportCopyReturn = {
  /**
   * Returns the outcome as well as toasting it. The menu ignores the return
   * value; `ui_export_results` needs it, because an agent that is told an
   * export succeeded when the user cancelled the save dialog will go on to
   * describe a file that does not exist.
   */
  handleExport: (args: {
    format: ResultsExportFormat;
    scope: 'active' | 'all';
  }) => Promise<GridExportOutcome>;
  handleCopy: (format: 'csv' | 'markdown' | 'insert' | 'html') => Promise<void>;
  isLocalExporting: boolean;
  copied: boolean;
  showExportLimitDialog: boolean;
  setShowExportLimitDialog: (show: boolean) => void;
  exportLimitRowCount: number;
};

export function useExportCopy({
  exportableResultSets,
  activeExportResultSetIndex,
  canExport,
  canExportFull,
  engineId,
  isWindowed,
  wdBufferRows,
  wdBufferStart,
  activeResultSetColumns,
  effectiveTotalRowCount,
  getCellValueFn,
  sourceGeneration,
  nativeSpatialColumnIds,
  connectionId,
  database,
}: UseExportCopyParams): UseExportCopyReturn {
  const hasSpatialColumns = !!nativeSpatialColumnIds && nativeSpatialColumnIds.size > 0;
  const skipIdentity = useAppStore((state) => state.insertSkipIdentity);
  const [isLocalExporting, setIsLocalExporting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showExportLimitDialog, setShowExportLimitDialog] = useState(false);
  const [exportLimitRowCount, setExportLimitRowCount] = useState(0);

  /**
   * D2 — resolve the IDENTITY columns of the result set's source table so
   * generated INSERTs either wrap MSSQL output in `SET IDENTITY_INSERT ON/OFF`
   * (default) or drop the identity columns ("Skip identity columns").
   *
   * ONE light metadata fetch per copy/export action — and only when it can
   * change the output: with the default mode there is nothing to wrap on
   * non-MSSQL engines, so those skip the catalog round-trip entirely. When
   * nothing resolves (join, unresolvable table) no `identityColumns` come
   * back, and the builder emits exactly what it emitted before identity
   * awareness existed.
   *
   * FIX-1 (CP-2) — loud degradation. `fetchIdentityColumnsForResultSet`
   * returns `null` (not `[]`) when the table WAS resolved but the check
   * itself failed (no API / `success:false` / throw / missing connection
   * context). We only ever reach that fetch when it "can matter" (the toggle
   * is on, or the engine is MSSQL where the wrapper is the default) — see the
   * early return above — so a `null` here always means the user could be
   * about to copy/export an unwrapped, identity-bearing INSERT without
   * knowing it. Warn instead of silently changing behavior.
   */
  const resolveIdentityOptions = useCallback(
    async (
      columns: QueryColumn[],
    ): Promise<{ identityColumns?: string[]; identityMode: InsertIdentityMode }> => {
      const identityMode: InsertIdentityMode = skipIdentity ? 'exclude' : 'include-wrapped';
      // `identityMode` alone never changes the output — it always rides along so
      // the export request records the user's choice for analytics.
      if (!skipIdentity && engineId !== 'mssql') return { identityMode };

      const identityColumns = await fetchIdentityColumnsForResultSet({ connectionId, database, columns });
      if (identityColumns === null) {
        toast.warning({
          title: 'Could not check identity columns',
          description: 'The INSERT script was generated without identity handling.',
        });
        return { identityMode };
      }
      if (identityColumns.length === 0) return { identityMode };

      return { identityColumns, identityMode };
    },
    [skipIdentity, engineId, connectionId, database],
  );

  const handleExport = useCallback(
    async (args: { format: ResultsExportFormat; scope: 'active' | 'all' }): Promise<GridExportOutcome> => {
      // In windowed mode the `exportableResultSets` entry may not carry the
      // buffered rows directly, so we synthesize an "active" result set from
      // the current window buffer (full fidelity via getCellValueFn when the
      // buffer holds preview-truncated values). This preserves the "export the
      // visible slice" contract that the non-windowed path has always offered.
      if (!canExport && !isWindowed) return { success: false, error: 'No results to export' };

      let active: QueryResultSet | undefined;
      if (isWindowed && args.scope === 'active') {
        const bufRows = wdBufferRows;
        if (bufRows.length === 0) {
          toast.warning({ title: 'Nothing to export', description: 'Wait for rows to load before exporting.' });
          return { success: false, error: 'No rows have loaded yet' };
        }
        let finalRows = bufRows;
        if (getCellValueFn) {
          const full = await fetchFullBufferRows(bufRows, activeResultSetColumns, wdBufferStart, getCellValueFn, sourceGeneration);
          finalRows = full.rows;
        }
        active = { columns: activeResultSetColumns, rows: finalRows, rowCount: finalRows.length };
      } else {
        active = exportableResultSets[activeExportResultSetIndex];
      }
      if (!active) return { success: false, error: 'No results to export' };

      const resultSetsToExport = args.scope === 'all' ? exportableResultSets : [active];
      if ((args.format === 'csv' || args.format === 'markdown') && resultSetsToExport.length !== 1) {
        toast.error({ title: `${args.format.toUpperCase()} export not supported`, description: 'Export a single result set.' });
        return { success: false, error: `${args.format.toUpperCase()} export requires a single result set` };
      }

      const totalRows = calculateTotalExportRows(resultSetsToExport);
      if (!canExportFull) {
        const limitCheck = checkExportLimit(totalRows);
        if (limitCheck.exceedsLimit) {
          setExportLimitRowCount(totalRows);
          setShowExportLimitDialog(true);
          return {
            success: false,
            error: `${limitCheck.description} The app is showing the user the upgrade dialog.`,
          };
        }
      }

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const resultSetSuffix =
        args.scope === 'active' && exportableResultSets.length > 1
          ? `-result-${activeExportResultSetIndex + 1}`
          : args.scope === 'all'
            ? '-all'
            : '';

      const extension = args.format === 'markdown' ? 'md' : args.format;
      const suggestedFileName = `results${resultSetSuffix}-${timestamp}.${extension}`;

      // D2 — identity awareness applies to the SQL (INSERT) export only.
      const identityOptions =
        args.format === 'sql' ? await resolveIdentityOptions(active.columns ?? []) : {};

      setIsLocalExporting(true);
      try {
        const result = await exportResults({
          format: args.format,
          resultSets: resultSetsToExport,
          suggestedFileName,
          engineId,
          ...identityOptions,
          // T5 — WKT export parity (CSV/XLSX only; other formats ignore this).
          spatialColumnIds: hasSpatialColumns ? [...nativeSpatialColumnIds!] : undefined,
          title:
            args.format === 'csv'
              ? 'Export Results (CSV)'
              : args.format === 'json'
                ? 'Export Results (JSON)'
                : args.format === 'markdown'
                  ? 'Export Results (Markdown)'
                  : args.format === 'sql'
                    ? 'Export Results (SQL INSERT)'
                    : 'Export Results (Excel)',
        });

        if (result.success) {
          showExportSuccessToast(result.filePath, 'query-editor');
          return { success: true, filePath: result.filePath, rowCount: totalRows };
        }

        if (!result.cancelled) {
          toast.error({ title: 'Export failed', description: result.error });
        }
        return { success: false, error: result.error, cancelled: result.cancelled };
      } finally {
        setIsLocalExporting(false);
      }
    },
    [activeExportResultSetIndex, canExport, canExportFull, exportableResultSets, engineId, isWindowed, wdBufferRows, wdBufferStart, activeResultSetColumns, getCellValueFn, sourceGeneration, hasSpatialColumns, nativeSpatialColumnIds, resolveIdentityOptions]
  );

  const handleCopy = useCallback(async (format: 'csv' | 'markdown' | 'insert' | 'html') => {
    if (!canExport && !isWindowed) return;

    const bufRows = wdBufferRows;
    const cols = activeResultSetColumns;

    if (isWindowed && bufRows.length === 0) {
      toast.warning({ title: 'Nothing to copy', description: 'Wait for rows to load before copying.' });
      return;
    }

    let finalRows = bufRows;
    let usedPreview = false;
    if (isWindowed && getCellValueFn) {
      const result = await fetchFullBufferRows(bufRows, cols, wdBufferStart, getCellValueFn, sourceGeneration);
      finalRows = result.rows;
      usedPreview = result.usedPreview;
    }

    const active: QueryResultSet | undefined = isWindowed
      ? { columns: cols, rows: finalRows, rowCount: finalRows.length }
      : exportableResultSets[activeExportResultSetIndex];
    if (!active) return;

    if (format === 'insert' && active.rows && active.rows.length > 10000) {
      toast.warning({ title: 'Large result set', description: 'INSERT copy is limited to 10,000 rows. Use Export SQL for larger sets.' });
      return;
    }

    // T5 — WKT copy parity: decode native-spatial cells before any of the
    // clipboard formatters read them. Injected once, here, at the lowest
    // shared point every format (csv/markdown/insert/html) reads from.
    let activeForCopy = active;
    if (hasSpatialColumns && engineId) {
      const decoder = await loadSpatialDecoder();
      const dataTypeOf = (columnId: string): string | undefined =>
        active.columns?.find((c) => (c.id ?? c.name) === columnId)?.dataType;
      const transformedRows = (active.rows ?? []).map((row) => {
        let changed = false;
        const next: Record<string, unknown> = { ...row };
        for (const columnId of nativeSpatialColumnIds!) {
          if (!(columnId in row)) continue;
          // Cast is safe: an unrecognized engine id simply misses the decode
          // registry lookup inside transformSpatialForExport (no-op passthrough).
          const transformed = decoder.transformSpatialForExport(row[columnId], engineId as DatabaseEngineId, dataTypeOf(columnId));
          if (transformed !== row[columnId]) {
            next[columnId] = transformed;
            changed = true;
          }
        }
        return changed ? next : row;
      });
      activeForCopy = { ...active, rows: transformedRows };
    }

    // D2 — identity awareness applies to the INSERT copy only.
    const identityOptions = format === 'insert' ? await resolveIdentityOptions(active.columns ?? []) : undefined;

    try {
      if (format === 'html') {
        const html = resultSetToHtml(activeForCopy);
        const text = resultSetToCsv(activeForCopy);
        await writeRichToClipboard(text, html);
      } else {
        const content = format === 'markdown'
          ? resultSetToMarkdown(activeForCopy)
          : format === 'insert'
            ? resultSetToInsert(activeForCopy, { engineId, ...identityOptions })
            : resultSetToCsv(activeForCopy);
        if (!(await writeClipboardText(content))) return;
      }
      window.electronAPI?.analytics?.track('feature:results_copy', {
        format,
        // Only meaningful for INSERT copies — omitted elsewhere to keep the
        // event's props low-cardinality.
        ...(format === 'insert' ? { skip_identity: skipIdentity } : {}),
      }).catch(() => {});
      setCopied(true);
      if (isWindowed) {
        const truncNote = usedPreview ? ' Cell values truncated to 1KB. Use Export for full fidelity.' : '';
        toast.success({ title: 'Copied', description: `${finalRows.length.toLocaleString()} buffered rows copied (${effectiveTotalRowCount.toLocaleString()} total).${truncNote} Use Export All to CSV for the full dataset.` });
      }
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API not available
    }
  }, [activeExportResultSetIndex, activeResultSetColumns, canExport, effectiveTotalRowCount, exportableResultSets, engineId, isWindowed, wdBufferRows, wdBufferStart, getCellValueFn, sourceGeneration, hasSpatialColumns, nativeSpatialColumnIds, resolveIdentityOptions, skipIdentity]);

  return {
    handleExport,
    handleCopy,
    isLocalExporting,
    copied,
    showExportLimitDialog,
    setShowExportLimitDialog,
    exportLimitRowCount,
  };
}
